import React, { useState } from 'react';

import {
  type SellableStudioPlanId,
  type StudioBillingInterval,
  toPlanChangeSelection,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { useStudioPlanChange } from 'src/routes/settings/plans/components/studio/useStudioPlanChange';
import {
  type StudioPlanId,
  useGetExtraAppsPreviewQuery,
  useGetStudioAppUsageQuery,
  useGetStudioPlansQuery,
  usePurchaseExtraAppMutation,
} from 'src/services/api/studioCreditApi';
import { readIsProxySession } from 'src/utils/proxySession';

import {
  type AppLimitSurface,
  COMBINED_DEFAULT_INTERVAL,
  DEFAULT_UPGRADE_PLAN,
  getAppLimitRoute,
  getCombinedQuote,
  getExtraAppsAfterUpgrade,
  getUpgradePlanOptions,
} from './appLimit';
import type { AppLimitApp } from './AppLimitAppCard';
import { AppLimitCombinedModal } from './AppLimitCombinedModal';
import { AppLimitModal } from './AppLimitModal';
import type { AppLimitBackTo } from './AppLimitModalShell';
import { AppLimitUpgradeModal } from './AppLimitUpgradeModal';

interface AppLimitGateProps {
  onClose: () => void;
  /**
   * Resumes what the paywall interrupted — the build, or the pending app add.
   * Called only once the way past the limit has actually been bought: the
   * per-app fee charged, or the upgrade applied.
   */
  onProceed: () => void;
  /** The workspace's plan; decides which route out it is offered. */
  studioPlan: StudioPlanId;
  /** Which action the limit interrupted. */
  surface: AppLimitSurface;
  /** The app being held back, shown as the modal's card. */
  app?: AppLimitApp;
  /** Exact page URL to restore when the upgrade route uses Stripe checkout. */
  checkoutReturnUrl?: string;
  /**
   * Way back to the dialog this paywall replaced. Given only by surfaces that
   * hide themselves to make room for it — elsewhere there is nothing to return
   * to and the modal renders without the row.
   */
  backTo?: AppLimitBackTo;
}

/** Free is sold monthly only, so its picker opens on the monthly tab. */
const DEFAULT_INTERVAL: StudioBillingInterval = 'month';

/**
 * Owns what happens after the app-limit paywall.
 *
 * The fee route charges before it builds: confirming posts the purchase and the
 * modal stays open under a loading CTA until Stripe answers, so a declined card
 * is shown here — with the action still pending and retryable — instead of the
 * app being built and installed for free while the charge fails somewhere behind
 * it. Only a settled charge calls `onProceed`.
 *
 * The upgrade route submits the picked plan through the plans page's own
 * plan-change flow (`useStudioPlanChange`), which sends a Freemium workspace to
 * Stripe checkout because it has no card on file. One upgrade implementation,
 * shared with the settings page. An upgrade that applies without checkout —
 * a card already on file — resumes the interrupted action, since the larger
 * allowance it just bought is what was blocking it.
 */
export const AppLimitGate: React.FC<AppLimitGateProps> = ({
  onClose,
  onProceed,
  studioPlan,
  surface,
  app,
  checkoutReturnUrl,
  backTo,
}) => {
  // Set when the charge is refused, so the modal can say so and let the user try
  // again rather than closing on a failure the action depended on.
  const [hasChargeFailed, setHasChargeFailed] = useState(false);

  const [purchaseExtraApp, { isLoading: isCharging }] =
    usePurchaseExtraAppMutation();

  // Cached by the surface that raised the paywall, so this reads no network.
  const { data: usage } = useGetStudioAppUsageQuery();

  // One route out per plan, keyed off the plan the rest of the UI shows rather
  // than the usage read's `canAddPaidExtras`: that flag follows the Stripe
  // subscription, so a workspace on a paid plan without one would be pitched an
  // upgrade it does not need. Free is the only plan the backend hard-blocks from
  // extra apps; whether a paid plan alone makes room depends on its allowance.
  const countableApps = usage?.countableApps ?? 0;
  const route = getAppLimitRoute(studioPlan, countableApps);

  const [interval, selectInterval] = useState<StudioBillingInterval>(
    route === 'combined' ? COMBINED_DEFAULT_INTERVAL : DEFAULT_INTERVAL,
  );
  const [selectedPlan, setSelectedPlan] =
    useState<SellableStudioPlanId>(DEFAULT_UPGRADE_PLAN);

  // An upgrade that applied without checkout (a card already on file) bought no
  // app on the combined route, so the app is charged here before the action
  // resumes — the same charge-first order the fee route keeps.
  const completeUpgrade = () => {
    if (route !== 'combined') {
      onProceed();
      return;
    }
    void (async () => {
      setHasChargeFailed(false);
      try {
        await purchaseExtraApp().unwrap();
      } catch {
        setHasChargeFailed(true);
        return;
      }
      onProceed();
    })();
  };

  const { submitUpgrade, isUpgrading } = useStudioPlanChange({
    onUpgradeComplete: completeUpgrade,
    checkoutReturnUrl,
  });

  // Only fetched where a plan is on offer; it prices the picker and the bundle.
  const { data } = useGetStudioPlansQuery(undefined, {
    skip: route === 'fee',
  });

  // Stripe's quote for one more app, read only where the fee is on offer — the
  // endpoint rejects Free outright. Normally already cached: `useAppLimit` warms
  // it on the surfaces that can open this modal, so the price paints on open
  // rather than after a proration round-trip. Still re-quoted on mount, since the
  // amount due now prorates against the time left in the period — but that runs
  // behind the cached price instead of a skeleton.
  const {
    data: feePreview,
    isError: isFeeUnquoted,
    refetch: refetchFeePreview,
  } = useGetExtraAppsPreviewQuery(undefined, {
    skip: route !== 'fee',
    refetchOnMountOrArgChange: true,
  });

  if (route === 'combined') {
    // The cheapest paid plan: it carries the same allowance as the rest, so
    // the bundle is pitched at the smallest step that unlocks the app.
    const [cheapest] = getUpgradePlanOptions(data?.plans);

    return (
      <AppLimitCombinedModal
        onClose={onClose}
        plan={cheapest?.planId ?? 'starter'}
        quote={getCombinedQuote(
          cheapest?.tier,
          data?.extraApp,
          interval,
          getExtraAppsAfterUpgrade(countableApps),
        )}
        interval={interval}
        onChangeInterval={selectInterval}
        includedApps={usage?.includedApps ?? 0}
        countableApps={countableApps}
        isSubmitting={isUpgrading || isCharging}
        hasChargeFailed={hasChargeFailed}
        surface={surface}
        backTo={backTo}
        onConfirm={() => {
          if (!cheapest || readIsProxySession()) return;
          void submitUpgrade(
            toPlanChangeSelection(cheapest.planId, interval, cheapest.tier),
            { includeExtraApp: true },
          );
        }}
      />
    );
  }

  if (route === 'upgrade') {
    const options = getUpgradePlanOptions(data?.plans);
    const chosen = options.find((option) => option.planId === selectedPlan);

    return (
      <AppLimitUpgradeModal
        onClose={onClose}
        options={options}
        selectedPlan={selectedPlan}
        onSelectPlan={setSelectedPlan}
        interval={interval}
        onChangeInterval={selectInterval}
        isSubmitting={isUpgrading}
        surface={surface}
        backTo={backTo}
        onConfirm={() => {
          // The CTA only enables once the catalog carries the picked plan, so
          // this is the type narrowing rather than a state the user can reach.
          if (!chosen || readIsProxySession()) return;
          void submitUpgrade(
            toPlanChangeSelection(chosen.planId, interval, chosen.tier),
          );
        }}
      />
    );
  }

  // Charge, then hand back. The modal is deliberately left open across the await:
  // it is the only thing telling the user their click is still in flight, and on
  // a refusal it is where the reason belongs.
  const takeFeeThenProceed = async () => {
    if (readIsProxySession()) {
      return;
    }
    setHasChargeFailed(false);
    try {
      await purchaseExtraApp().unwrap();
    } catch {
      setHasChargeFailed(true);
      return;
    }
    onProceed();
  };

  return (
    <AppLimitModal
      onClose={onClose}
      isConfirming={isCharging}
      hasChargeFailed={hasChargeFailed}
      feePreview={feePreview}
      isFeeUnquoted={isFeeUnquoted}
      onRetryFee={() => {
        void refetchFeePreview();
      }}
      surface={surface}
      app={app}
      backTo={backTo}
      onConfirm={() => {
        void takeFeeThenProceed();
      }}
    />
  );
};
